import { createBoard, type Board, type Element, type GridPos } from "./board";
import { ELEMENTS, Rng } from "./balance";

type Troop = { element: Element; tier: number };
type Formation = "cluster" | "spread" | "escort" | "triad" | "flanks";
type Squad = [element: Element, tier: number, count: number];
type Encounter = { formation: Formation; squads: Squad[] };

// Each opening encounter teaches a different roster shape; strength rises from 1 to 48.
const OPENING: Encounter[] = [
  { formation: "cluster", squads: [["fire", 1, 1]] },
  { formation: "spread", squads: [["fire", 1, 1], ["ice", 1, 1]] },
  { formation: "triad", squads: [["fire", 1, 1], ["ice", 1, 1], ["lightning", 1, 1]] },
  { formation: "cluster", squads: [["fire", 1, 5]] }, // Little fire swarm.
  { formation: "escort", squads: [["ice", 3, 1], ["ice", 1, 4]] },
  { formation: "flanks", squads: [["lightning", 3, 2], ["lightning", 1, 2]] },
  { formation: "escort", squads: [["fire", 4, 1], ["ice", 2, 1], ["lightning", 2, 1], ["fire", 1, 2]] },
  { formation: "cluster", squads: [["fire", 1, 6], ["ice", 1, 6], ["lightning", 1, 6]] },
  { formation: "escort", squads: [["lightning", 5, 1], ["lightning", 1, 4]] }, // Storm giant and sparks.
  { formation: "flanks", squads: [["ice", 5, 1], ["fire", 3, 2], ["ice", 2, 2]] },
  { formation: "spread", squads: [["fire", 5, 1], ["ice", 5, 1], ["lightning", 2, 2]] },
  { formation: "triad", squads: [["fire", 5, 1], ["ice", 5, 1], ["lightning", 5, 1]] },
];

const at = (x: number, y: number): GridPos => ({ x, y });
const centerCells = Array.from({ length: 64 }, (_, i) => at(2 + i % 8, 2 + Math.floor(i / 8)))
  .sort((a, b) => (Math.abs(a.x - 5) + Math.abs(a.y - 5)) - (Math.abs(b.x - 5) + Math.abs(b.y - 5)) || a.y - b.y || a.x - b.x);
const formations: Record<Formation, GridPos[]> = {
  cluster: centerCells,
  spread: [at(5,5), at(2,2), at(8,8), at(8,2), at(2,8), at(5,2), at(2,5), at(8,5), at(5,8)],
  escort: [at(5,5), at(3,5), at(7,5), at(5,3), at(5,7), at(3,3), at(7,7), at(7,3), at(3,7)],
  triad: [at(3,4), at(8,4), at(5,8)],
  flanks: [at(3,5), at(8,5), at(3,3), at(8,3), at(3,7), at(8,7), at(3,9), at(8,9)],
};

const floorPower = (value: number): number => 2 ** Math.floor(Math.log2(Math.max(1, value)));
const ceilPower = (value: number): number => 2 ** Math.ceil(Math.log2(Math.max(1, value)));

function laterEncounter(level: number, rng: Rng): { troops: Troop[]; formation: Formation } {
  // Roughly double total HP/damage every five levels, regardless of roster shape.
  // Nine significant binary digits keep any leftover budget representable in <=24 pieces.
  const raw = Math.min(2 ** 50, 48 * 2 ** ((level - 12) / 5));
  const quantum = 2 ** Math.max(0, Math.floor(Math.log2(raw)) - 8);
  const budget = Math.round(raw / quantum) * quantum;
  let remaining = budget;
  const troops: Troop[] = [];
  const main = Math.floor(rng.next() * ELEMENTS.length);
  // Pick a seeded theme from alternating pools, avoiding repeated neighboring identities.
  const themes: Formation[] = level % 2 ? ["cluster", "triad", "spread"] : ["escort", "flanks"];
  const formation = themes[Math.floor(rng.next() * themes.length)];
  const add = (element: Element, power: number) => {
    troops.push({ element, tier: 1 + Math.round(Math.log2(power)) });
    remaining -= power;
  };
  if (formation === "escort") add(ELEMENTS[main], floorPower(budget * 0.6));
  if (formation === "triad") ELEMENTS.forEach(element => add(element, floorPower(budget / 3)));
  if (formation === "flanks") {
    add(ELEMENTS[main], floorPower(budget / 4));
    add(ELEMENTS[(main + 1) % 3], floorPower(budget / 4));
  }
  const minionPower = ceilPower(remaining / (formation === "cluster" ? 18 : 10));
  while (remaining > 0) {
    let power = Math.min(floorPower(remaining), minionPower);
    if (formation === "spread") power = Math.max(quantum, floorPower(remaining / (3 + Math.floor(rng.next() * 5))));
    if (troops.length >= 15) power = floorPower(remaining);
    const element = formation === "escort" ? ELEMENTS[main] : formation === "flanks" ? ELEMENTS[(main + troops.length % 2) % 3] :
      formation === "cluster" && rng.next() < 0.75 ? ELEMENTS[main] : ELEMENTS[Math.floor(rng.next() * 3)];
    add(element, power);
  }
  return { troops, formation };
}

export function createOpponentBoard(level: number): Board {
  const rng = new Rng(level * 7919);
  const authored = OPENING[level - 1];
  const { troops, formation } = authored ? {
    troops: authored.squads.flatMap(([element, tier, count]) => Array.from({ length: count }, () => ({ element, tier }))),
    formation: authored.formation,
  } : laterEncounter(level, rng);
  const positions = [...formations[formation], ...centerCells].filter((pos, index, all) =>
    all.findIndex(other => other.x === pos.x && other.y === pos.y) === index);
  const mirror = !authored && rng.next() < 0.5;
  const transpose = !authored && rng.next() < 0.5;
  const board = createBoard();
  troops.forEach((troop, i) => {
    let { x, y } = positions[i];
    if (mirror) x = 11 - x;
    if (transpose) [x, y] = [y, x];
    board.cells[y * board.width + x] = { id: `encounter-${level}-${i}`, ...troop };
  });
  return board;
}
